import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  CanvasManualAdjustmentModel,
  Schema,
  SchemaUpdateRequest,
  apiService,
} from '../../services/api';
import {
  toRecord,
  cleanObject,
  arrayToInputString,
  normaliseIdentifier,
  normaliseArrayInput,
} from '../../utils/dataUtils';
import type { ToastState } from './types';

export interface EnrichmentFormParams {
  draft: Schema | undefined;
  tablesDefinition: Record<string, unknown>;
  tableNames: string[];
  tableEnrichmentMap: Record<string, unknown>;
  fieldEnrichmentMap: Record<string, unknown>;
  tableSnapshotMap: Record<string, unknown>;
  fieldSnapshotMap: Record<string, unknown>;
  sortedSchemaAdjustments: CanvasManualAdjustmentModel[];
  ledgerWorkbookId: string;
  updateDraftMetadataMutation: {
    isLoading: boolean;
    mutateAsync: (payload: SchemaUpdateRequest) => Promise<unknown>;
  };
  setToast: (value: ToastState | null) => void;
}

export function useEnrichmentForm(params: EnrichmentFormParams) {
  const {
    draft,
    tablesDefinition,
    tableNames,
    tableEnrichmentMap,
    fieldEnrichmentMap,
    tableSnapshotMap,
    fieldSnapshotMap,
    sortedSchemaAdjustments,
    ledgerWorkbookId,
    updateDraftMetadataMutation,
    setToast,
  } = params;

  const queryClient = useQueryClient();

  const [selectedTable, setSelectedTable] = useState<string>('');
  const [tableDescription, setTableDescription] = useState<string>('');
  const [tableOwners, setTableOwners] = useState<string>('');
  const [tableTags, setTableTags] = useState<string>('');
  const [tableClassification, setTableClassification] = useState<string>('');
  const [tableSensitivity, setTableSensitivity] = useState<string>('');

  const [selectedFieldTable, setSelectedFieldTable] = useState<string>('');
  const [selectedFieldName, setSelectedFieldName] = useState<string>('');
  const [fieldDescription, setFieldDescription] = useState<string>('');
  const [fieldOwners, setFieldOwners] = useState<string>('');
  const [fieldTags, setFieldTags] = useState<string>('');
  const [fieldClassification, setFieldClassification] = useState<string>('');
  const [fieldSensitivity, setFieldSensitivity] = useState<string>('');

  const [enrichmentSaving, setEnrichmentSaving] = useState<boolean>(false);

  const selectedTableHistory = useMemo<CanvasManualAdjustmentModel[]>(() => {
    if (!selectedTable) {
      return [];
    }
    const target = normaliseIdentifier(selectedTable);
    return sortedSchemaAdjustments.filter(
      (entry) =>
        entry.target_kind === 'schema_table' && normaliseIdentifier(entry.target_id) === target
    );
  }, [sortedSchemaAdjustments, selectedTable]);

  const selectedFieldHistory = useMemo<CanvasManualAdjustmentModel[]>(() => {
    if (!selectedFieldTable || !selectedFieldName) {
      return [];
    }
    const target = normaliseIdentifier(`${selectedFieldTable}.${selectedFieldName}`);
    return sortedSchemaAdjustments.filter(
      (entry) =>
        entry.target_kind === 'schema_field' && normaliseIdentifier(entry.target_id) === target
    );
  }, [sortedSchemaAdjustments, selectedFieldTable, selectedFieldName]);

  const latestTableAdjustment = selectedTableHistory[0];
  const latestFieldAdjustment = selectedFieldHistory[0];

  const selectedTableColumns = useMemo(() => {
    if (!selectedFieldTable) {
      return [];
    }
    const table = toRecord(tablesDefinition[selectedFieldTable]);
    const columns = Array.isArray(table.columns)
      ? (table.columns as Array<Record<string, unknown>>)
      : [];
    return columns
      .map((column) => {
        if (typeof column === 'string') {
          return column;
        }
        if (column && typeof column === 'object' && 'name' in column) {
          return column.name as string;
        }
        return JSON.stringify(column);
      })
      .filter((name): name is string => typeof name === 'string' && name.length > 0);
  }, [selectedFieldTable, tablesDefinition]);

  const selectedTableSnapshot = useMemo(
    () => toRecord(tableSnapshotMap[selectedTable]),
    [tableSnapshotMap, selectedTable]
  );
  const selectedFieldSnapshot = useMemo(() => {
    if (!selectedFieldTable || !selectedFieldName) {
      return {};
    }
    const key = `${selectedFieldTable}.${selectedFieldName}`;
    return toRecord(fieldSnapshotMap[key]);
  }, [fieldSnapshotMap, selectedFieldTable, selectedFieldName]);

  const tableSnapshotUpdatedAt = selectedTableSnapshot.updated_at as string | undefined;
  const tableSnapshotUpdatedBy = selectedTableSnapshot.updated_by as string | undefined;
  const fieldSnapshotUpdatedAt = selectedFieldSnapshot.updated_at as string | undefined;
  const fieldSnapshotUpdatedBy = selectedFieldSnapshot.updated_by as string | undefined;

  const tableLastUpdatedAt =
    tableSnapshotUpdatedAt ?? (latestTableAdjustment?.created_at as string | undefined);
  const tableLastUpdatedBy = tableSnapshotUpdatedBy ?? latestTableAdjustment?.author;
  const fieldLastUpdatedAt =
    fieldSnapshotUpdatedAt ?? (latestFieldAdjustment?.created_at as string | undefined);
  const fieldLastUpdatedBy = fieldSnapshotUpdatedBy ?? latestFieldAdjustment?.author;

  // Auto-select first table when available
  useEffect(() => {
    if (!selectedTable && tableNames.length > 0) {
      setSelectedTable(tableNames[0]);
    }
  }, [tableNames, selectedTable]);

  useEffect(() => {
    if (!selectedFieldTable && tableNames.length > 0) {
      setSelectedFieldTable(tableNames[0]);
    }
  }, [tableNames, selectedFieldTable]);

  useEffect(() => {
    if (selectedFieldTable && selectedTableColumns.length > 0 && !selectedFieldName) {
      setSelectedFieldName(selectedTableColumns[0]);
    }
    if (selectedFieldTable && selectedTableColumns.length === 0) {
      setSelectedFieldName('');
    }
  }, [selectedFieldTable, selectedFieldName, selectedTableColumns]);

  // Hydrate table enrichment form when selection changes
  useEffect(() => {
    if (!selectedTable) {
      return;
    }
    const table = toRecord(tablesDefinition[selectedTable]);
    const tableEnrichment = toRecord(tableEnrichmentMap[selectedTable]);
    const tableSnapshotRecord = toRecord(tableSnapshotMap[selectedTable]);
    const tableSnapshotValues = toRecord(tableSnapshotRecord.values);
    const ledgerEnrichment = toRecord(toRecord(latestTableAdjustment?.details).enrichment);

    const description =
      tableSnapshotValues.description ??
      tableEnrichment.description ??
      ledgerEnrichment.description ??
      table.description ?? '';
    const owners =
      tableSnapshotValues.owners ??
      tableSnapshotValues.owner ??
      tableEnrichment.owners ??
      tableEnrichment.owner ??
      ledgerEnrichment.owners ??
      ledgerEnrichment.owner ??
      table.owners ??
      table.owner ??
      '';
    const tags =
      tableSnapshotValues.tags ??
      tableEnrichment.tags ??
      ledgerEnrichment.tags ??
      table.tags ??
      [];
    const classification =
      tableSnapshotValues.classification ??
      tableEnrichment.classification ??
      ledgerEnrichment.classification ??
      table.classification ??
      '';
    const sensitivity =
      tableSnapshotValues.sensitivity ??
      tableSnapshotValues.data_classification ??
      tableEnrichment.sensitivity ??
      tableEnrichment.data_classification ??
      ledgerEnrichment.sensitivity ??
      ledgerEnrichment.data_classification ??
      table.sensitivity ??
      table.data_classification ??
      '';

    setTableDescription(String(description ?? ''));
    setTableOwners(arrayToInputString(owners));
    setTableTags(arrayToInputString(tags));
    setTableClassification(String(classification ?? ''));
    setTableSensitivity(String(sensitivity ?? ''));
  }, [selectedTable, tablesDefinition, tableEnrichmentMap, tableSnapshotMap, latestTableAdjustment]);

  // Hydrate field enrichment form when selection changes
  useEffect(() => {
    if (!selectedFieldTable || !selectedFieldName) {
      return;
    }
    const table = toRecord(tablesDefinition[selectedFieldTable]);
    const columns = Array.isArray(table.columns)
      ? (table.columns as Array<Record<string, unknown>>)
      : [];
    const field =
      columns.find((column) => (column as Record<string, unknown>)?.name === selectedFieldName) ??
      ({} as Record<string, unknown>);
    const fieldKey = `${selectedFieldTable}.${selectedFieldName}`;
    const fieldEnrichment = toRecord(fieldEnrichmentMap[fieldKey]);
    const fieldSnapshotRecord = toRecord(fieldSnapshotMap[fieldKey]);
    const fieldSnapshotValues = toRecord(fieldSnapshotRecord.values);
    const ledgerEnrichment = toRecord(toRecord(latestFieldAdjustment?.details).enrichment);

    const description =
      fieldSnapshotValues.description ??
      fieldEnrichment.description ??
      ledgerEnrichment.description ??
      (field as Record<string, unknown>).description ?? '';
    const owners =
      fieldSnapshotValues.owners ??
      fieldSnapshotValues.owner ??
      fieldEnrichment.owners ??
      fieldEnrichment.owner ??
      ledgerEnrichment.owners ??
      ledgerEnrichment.owner ??
      (field as Record<string, unknown>).owners ??
      (field as Record<string, unknown>).owner ??
      '';
    const tags =
      fieldSnapshotValues.tags ??
      fieldSnapshotValues.semantic_tags ??
      fieldEnrichment.tags ??
      fieldEnrichment.semantic_tags ??
      ledgerEnrichment.tags ??
      ledgerEnrichment.semantic_tags ??
      (field as Record<string, unknown>).tags ??
      (field as Record<string, unknown>).semantic_tags ??
      [];
    const classification =
      fieldSnapshotValues.classification ??
      fieldEnrichment.classification ??
      ledgerEnrichment.classification ??
      (field as Record<string, unknown>).classification ??
      '';
    const sensitivity =
      fieldSnapshotValues.sensitivity ??
      fieldSnapshotValues.pii_classification ??
      fieldEnrichment.sensitivity ??
      fieldEnrichment.pii_classification ??
      ledgerEnrichment.sensitivity ??
      ledgerEnrichment.pii_classification ??
      (field as Record<string, unknown>).sensitivity ??
      (field as Record<string, unknown>).pii_classification ??
      '';

    setFieldDescription(String(description ?? ''));
    setFieldOwners(arrayToInputString(owners));
    setFieldTags(arrayToInputString(tags));
    setFieldClassification(String(classification ?? ''));
    setFieldSensitivity(String(sensitivity ?? ''));
  }, [
    selectedFieldTable,
    selectedFieldName,
    tablesDefinition,
    fieldEnrichmentMap,
    fieldSnapshotMap,
    latestFieldAdjustment,
  ]);

  const recordManualAdjustment = useCallback(
    async (
      targetKind: 'schema_table' | 'schema_field',
      targetId: string,
      rationale: string,
      details: Record<string, unknown>,
      annotations: Record<string, unknown> = {}
    ) => {
      if (!ledgerWorkbookId) {
        console.warn('Skipping manual adjustment logging; no schema set/workbook context available.');
        return;
      }
      try {
        await apiService.createCanvasManualAdjustment(ledgerWorkbookId, {
          target_id: targetId,
          target_kind: targetKind,
          author: 'web-ui',
          rationale,
          status: 'pending_review',
          annotations: cleanObject({
            schema_id: draft?.schema_id,
            project_id: draft?.project_id,
            target_schema_id: ledgerWorkbookId,
            source: 'schema-draft-workspace',
            ...annotations,
          }),
          details: cleanObject(details),
        });
        await queryClient.invalidateQueries(['schema-ledger', ledgerWorkbookId]);
      } catch (error) {
        console.error('Failed to record manual adjustment ledger entry', error);
        setToast({
          severity: 'error',
          message: 'Enrichment saved, but ledger logging failed.',
        });
      }
    },
    [draft, ledgerWorkbookId, queryClient, setToast]
  );

  const handleSaveTableEnrichment = useCallback(async () => {
    if (!draft || !selectedTable) {
      return;
    }
    const existingMetadata = toRecord(draft.metadata);
    const enrichment = { ...toRecord(existingMetadata.enrichment) };
    const tablesMetadata = { ...toRecord(enrichment.tables) };

    const tableEntry = cleanObject({
      description: tableDescription.trim() || undefined,
      owners: normaliseArrayInput(tableOwners),
      tags: normaliseArrayInput(tableTags),
      classification: tableClassification.trim() || undefined,
      sensitivity: tableSensitivity.trim() || undefined,
    });

    if (Object.keys(tableEntry).length > 0) {
      tablesMetadata[selectedTable] = tableEntry;
    } else {
      delete tablesMetadata[selectedTable];
    }

    const nextEnrichment = cleanObject({
      ...enrichment,
      tables: Object.keys(tablesMetadata).length > 0 ? tablesMetadata : undefined,
      last_updated_at: new Date().toISOString(),
      last_updated_by: 'web-ui',
    });

    const nextMetadata = cleanObject({
      ...existingMetadata,
      enrichment: nextEnrichment,
    });

    setEnrichmentSaving(true);
    try {
      await updateDraftMetadataMutation.mutateAsync({ metadata: nextMetadata });
      await recordManualAdjustment(
        'schema_table',
        selectedTable,
        `Updated table enrichment metadata for ${selectedTable}`,
        { table: selectedTable, enrichment: tableEntry }
      );
      setToast({ severity: 'success', message: 'Table enrichment saved.' });
    } catch (error) {
      console.error('Failed to save table enrichment', error);
      setToast({
        severity: 'error',
        message: error instanceof Error ? error.message : 'Failed to save table enrichment.',
      });
    } finally {
      setEnrichmentSaving(false);
    }
  }, [
    draft,
    selectedTable,
    tableClassification,
    tableDescription,
    tableOwners,
    tableSensitivity,
    tableTags,
    recordManualAdjustment,
    updateDraftMetadataMutation,
    setToast,
  ]);

  const handleSaveFieldEnrichment = useCallback(async () => {
    if (!draft || !selectedFieldTable || !selectedFieldName) {
      return;
    }

    const existingMetadata = toRecord(draft.metadata);
    const enrichment = { ...toRecord(existingMetadata.enrichment) };
    const fieldsMetadata = { ...toRecord(enrichment.fields) };
    const fieldKey = `${selectedFieldTable}.${selectedFieldName}`;

    if (selectedTableColumns.length > 0 && !selectedTableColumns.includes(selectedFieldName)) {
      setToast({
        severity: 'error',
        message: `Column ${selectedFieldName} not found in table ${selectedFieldTable}.`,
      });
      return;
    }

    const fieldEntry = cleanObject({
      description: fieldDescription.trim() || undefined,
      owners: normaliseArrayInput(fieldOwners),
      tags: normaliseArrayInput(fieldTags),
      classification: fieldClassification.trim() || undefined,
      sensitivity: fieldSensitivity.trim() || undefined,
    });

    if (Object.keys(fieldEntry).length > 0) {
      fieldsMetadata[fieldKey] = fieldEntry;
    } else {
      delete fieldsMetadata[fieldKey];
    }

    const nextEnrichment = cleanObject({
      ...enrichment,
      fields: Object.keys(fieldsMetadata).length > 0 ? fieldsMetadata : undefined,
      last_updated_at: new Date().toISOString(),
      last_updated_by: 'web-ui',
    });

    const nextMetadata = cleanObject({
      ...existingMetadata,
      enrichment: nextEnrichment,
    });

    setEnrichmentSaving(true);
    try {
      await updateDraftMetadataMutation.mutateAsync({ metadata: nextMetadata });
      await recordManualAdjustment(
        'schema_field',
        fieldKey,
        `Updated field enrichment metadata for ${fieldKey}`,
        { table: selectedFieldTable, field: selectedFieldName, enrichment: fieldEntry }
      );
      setToast({ severity: 'success', message: 'Field enrichment saved.' });
    } catch (error) {
      console.error('Failed to save field enrichment', error);
      setToast({
        severity: 'error',
        message: error instanceof Error ? error.message : 'Failed to save field enrichment.',
      });
    } finally {
      setEnrichmentSaving(false);
    }
  }, [
    draft,
    fieldClassification,
    fieldDescription,
    fieldOwners,
    fieldSensitivity,
    fieldTags,
    selectedFieldName,
    selectedFieldTable,
    recordManualAdjustment,
    selectedTableColumns,
    updateDraftMetadataMutation,
    setToast,
  ]);

  return {
    // Table enrichment form
    selectedTable,
    setSelectedTable,
    tableDescription,
    setTableDescription,
    tableOwners,
    setTableOwners,
    tableTags,
    setTableTags,
    tableClassification,
    setTableClassification,
    tableSensitivity,
    setTableSensitivity,
    tableLastUpdatedAt,
    tableLastUpdatedBy,
    handleSaveTableEnrichment,

    // Field enrichment form
    selectedFieldTable,
    setSelectedFieldTable,
    selectedFieldName,
    setSelectedFieldName,
    selectedTableColumns,
    fieldDescription,
    setFieldDescription,
    fieldOwners,
    setFieldOwners,
    fieldTags,
    setFieldTags,
    fieldClassification,
    setFieldClassification,
    fieldSensitivity,
    setFieldSensitivity,
    fieldLastUpdatedAt,
    fieldLastUpdatedBy,
    handleSaveFieldEnrichment,

    // Shared
    enrichmentSaving,
  };
}
